import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { COMPANY, CAPITAL_TREE } from "@/lib/content";
import { Icon } from "@/lib/icons";
import { PillButton, Reveal } from "@/components/primitives";
import { subscribeNewsletter } from "@/lib/api";
import { Input } from "@/components/ui/input";

const COLUMNS = [
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Growth & Milestones", to: "/growth" },
      { label: "Careers", to: "/careers" },
      { label: "Contact Us", to: "/contact" },
    ],
  },
  {
    title: "Products",
    links: [
      { label: "Our Platforms", to: "/platforms" },
      { label: "What We Do", to: "/what-we-do" },
      { label: "Technologies", to: "/technologies" },
      { label: "Contact Us", to: "/contact" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Technology Architecture", to: "/technologies" },
      { label: "Security & Reliability", to: "/" },
      { label: "Industries We Serve", to: "/" },
      { label: "Contact Support", to: "/contact" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Use", to: "/terms-of-use" },
      { label: "Disclaimer", to: "/disclaimer" },
      { label: "Cookie Policy", to: "/cookie-policy" },
    ],
  },
];

function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      await subscribeNewsletter({ email });
      toast.success("Subscribed! Thanks for joining our updates.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-8 max-w-md" data-testid="newsletter-form">
      <p className="text-sm text-slate-400">Subscribe for company updates and product news.</p>
      <div className="mt-3 flex gap-3">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="bg-white/5 border-white/10"
          data-testid="newsletter-email"
        />
        <PillButton type="submit" testid="newsletter-submit" icon="ArrowRight">
          {loading ? "..." : "Subscribe"}
        </PillButton>
      </div>
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-matte border-t border-white/10 overflow-hidden" data-testid="site-footer">
      <div className="pointer-events-none absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-teal/10 blur-[130px]" />

      {/* Big statement */}
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-14 border-b border-white/10">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal mb-6">
            Let&rsquo;s Build Together
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl md:text-6xl font-heading font-semibold tracking-tight text-white max-w-4xl leading-[1.02]">
            Let&rsquo;s build the future of <span className="text-gradient-teal">digital infrastructure</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-4">
            <PillButton to="/contact" testid="footer-partner-cta">Partner With Us</PillButton>
            <PillButton to="/contact" variant="secondary" testid="footer-contact-cta">Contact Our Team</PillButton>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Newsletter />
        </Reveal>
      </div>

      {/* Columns */}
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
        <div className="lg:col-span-2 max-w-sm">
          <div className="flex items-center gap-2.5 mb-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal/15 border border-teal/40">
              <span className="text-teal font-heading font-extrabold text-lg">V</span>
            </span>
            <span className="font-heading font-bold text-white tracking-tight">{COMPANY.legalName}</span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            An Indian technology company building fintech software, trading evaluation
            technologies, business automation systems and scalable digital platforms.
          </p>
          <a
            href={CAPITAL_TREE.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-ctf-link"
            className="mt-5 inline-flex items-center gap-2 text-sm text-teal hover:text-teal-glow transition-colors"
          >
            <Icon name="ExternalLink" className="h-4 w-4" />
            Visit {CAPITAL_TREE.name}
          </a>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.title}>
            <h4 className="font-heading font-semibold text-white text-sm mb-4">{col.title}</h4>
            <ul className="space-y-3">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-slate-400 hover:text-teal transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Contact + CIN */}
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
        <div>
          <p className="text-slate-500 uppercase text-[11px] tracking-wider mb-1">Registered Office</p>
          <p className="text-slate-300">{COMPANY.registeredOffice}</p>
        </div>
        <div>
          <p className="text-slate-500 uppercase text-[11px] tracking-wider mb-1">Corporate Office</p>
          <p className="text-slate-300">{COMPANY.corporateOffice}</p>
        </div>
        <div>
          <p className="text-slate-500 uppercase text-[11px] tracking-wider mb-1">Email</p>
          <p className="text-slate-300">{COMPANY.email}</p>
        </div>
        <div>
          <p className="text-slate-500 uppercase text-[11px] tracking-wider mb-1">CIN</p>
          <p className="text-slate-300">{COMPANY.cin}</p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            &copy; {COMPANY.copyrightYear} {COMPANY.legalName}. All rights reserved.
          </p>
          <p className="text-xs text-slate-500 max-w-2xl md:text-right">
            {COMPANY.legalName} is a technology company. Platform-specific products and services are
            governed by their respective terms and disclosures.
          </p>
        </div>
      </div>
    </footer>
  );
}
